import { computed, inject, provide, ref, ComputedRef, Ref } from "vue";

export const SIDEBAR_COOKIE_NAME = "sidebar_state";
export const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
export const SIDEBAR_LOCAL_STORAGE_KEY = "sidebar_open";
export const SIDEBAR_WIDTH = "16rem";
export const SIDEBAR_WIDTH_MOBILE = "18rem";
export const SIDEBAR_WIDTH_ICON = "3rem";
export const SIDEBAR_KEYBOARD_SHORTCUT = "b";

export const SIDEBAR_INJECTION_KEY = Symbol("Sidebar");

export type SidebarContext = {
  state: ComputedRef<"expanded" | "collapsed">;
  open: Ref<boolean>;
  setOpen: (value: boolean) => void;
  isMobile: Ref<boolean>;
  openMobile: Ref<boolean>;
  setOpenMobile: (value: boolean) => void;
  toggleSidebar: () => void;
};

export function provideSidebarContext(context: SidebarContext): void {
  provide(SIDEBAR_INJECTION_KEY, context);
}

export function useSidebar(): SidebarContext {
  const context = inject<SidebarContext>(SIDEBAR_INJECTION_KEY);

  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

export function readSidebarDefaultOpen(): boolean {
  if (typeof window === "undefined") return true;

  const localStorageValue = window.localStorage.getItem(SIDEBAR_LOCAL_STORAGE_KEY);

  if (localStorageValue === "true") return true;
  if (localStorageValue === "false") return false;

  if (typeof document === "undefined") return true;

  return !document.cookie.includes(`${SIDEBAR_COOKIE_NAME}=false`);
}

export function useMediaQuery(query: string): Ref<boolean> {
  const matches = ref(false);

  if (typeof window === "undefined") return matches;

  const media = window.matchMedia(query);
  const update = () => {
    matches.value = media.matches;
  };

  update();

  if (media.addEventListener) {
    media.addEventListener("change", update);
  } else {
    media.addListener(update);
  }

  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", update);
      } else {
        media.removeListener(update);
      }
    });
  }

  return matches;
}
